import { cssVarV2 } from '@streamlain/theme/v2';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import clsx from 'clsx';
import type { KeyboardEventHandler, ReactNode } from 'react';

import * as styles from './style.css';

export interface StorageUsageBarProps {
  className?: string;
  color?: string | null;
  description?: ReactNode;
  fillTestId?: string;
  label: string;
  labelColor?: string;
  limitText: string;
  onAction?: () => void;
  percent: number | null;
  trackHeight?: number;
  usedText: string;
  valueTestId?: string;
  valueText?: string;
}

export const storageUsageColor = (percent: number | null): string | null => {
  if (percent === null) return null;
  if (percent >= 95) return cssVarV2('status/error');
  if (percent >= 80) return cssVarV2('status/warning' as any);
  return cssVarV2('toast/iconState/regular');
};

export const StorageUsageBar = ({
  className,
  color,
  description,
  fillTestId,
  label,
  labelColor = cssVarV2('text/secondary'),
  limitText,
  onAction,
  percent,
  trackHeight = 8,
  usedText,
  valueTestId,
  valueText,
}: StorageUsageBarProps) => {
  if (percent === null) return null;

  const clampedPercent = Math.min(100, Math.max(0, percent));
  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = event => {
    if (!onAction) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    onAction();
  };

  return (
    <div
      className={clsx(styles.root, className)}
      onClick={onAction}
      onKeyDown={handleKeyDown}
      role={onAction ? 'button' : undefined}
      style={assignInlineVars({
        [styles.fillColorVar]:
          color ??
          storageUsageColor(clampedPercent) ??
          cssVarV2('toast/iconState/regular'),
        [styles.labelColorVar]: labelColor,
        [styles.trackHeightVar]: `${trackHeight}px`,
      })}
      tabIndex={onAction ? 0 : undefined}
    >
      <div className={styles.labelRow}>
        <span className={styles.label}>{label}</span>
        <span className={styles.value} data-testid={valueTestId}>
          {valueText ?? (
            <>
              <span>{usedText}</span>
              <span> / </span>
              <span>{limitText}</span>
            </>
          )}
        </span>
      </div>
      <div className={styles.track}>
        <div
          className={styles.fill}
          data-testid={fillTestId}
          style={{ width: `${clampedPercent}%` }}
        />
      </div>
      {description ? (
        <div className={styles.description}>{description}</div>
      ) : null}
    </div>
  );
};
